import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { useUser } from "../contexts/useUser";
import { API } from "aws-amplify";
import { useQueryClient } from "@tanstack/react-query";
import { AgentDocumentsByAgentIdQuery } from "../API";
import { GraphQLResult } from "@aws-amplify/api";

const UrlScrape = ({
  agentId,
  projectId,
  documents,
}: {
  agentId: string;
  projectId: string;
  documents: GraphQLResult<AgentDocumentsByAgentIdQuery> | undefined;
}) => {
  const { user } = useUser();
  const [uploading, setUploading] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const [url, setUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const queryClient = useQueryClient();

  const [links, setLinks] = useState([]);

  const scrapeUrl = async (urlToScrape: string) => {
    setUploading(true);
    setProgressMessage("Scraping and indexing URL...");

    try {
      const r = await API.post("eventapi", "/scrapeUrlToAgent", {
        body: {
          projectId: projectId,
          agentId: agentId,
          location: urlToScrape,
          uploadDate: new Date().toISOString(),
          uploader: user?.displayName || "",
          type: "url",
        },
      });

      console.log("Scrape response", r);

      queryClient.invalidateQueries({
        queryKey: ["LLMAgentDocuments", { agentId: agentId }],
      });

      //setUrl("");
      //setUrlError("");
    } catch (error) {
      console.error("Error scraping url", error);
    } finally {
      setUploading(false);
      setProgressMessage("");
    }
  };

  const normalizeUrl = (url: string) => {
    return url.replace(/^https?:\/\/(www\.)?/, "");
  };

  const isUploadedAlready = (url: string) => {
    if (!documents) {
      return false;
    }

    const uploadedDocuments = documents.data?.agentDocumentsByAgentId?.items;

    if (!uploadedDocuments) {
      return false;
    }

    for (let i = 0; i < uploadedDocuments.length; i++) {
      if (
        normalizeUrl(uploadedDocuments[i]?.location ?? "") === normalizeUrl(url)
      ) {
        return true;
      }
    }

    return false;
  };

  return (
    <Box>
      <Dialog open={uploading} maxWidth="xs" fullWidth>
        <DialogTitle>Working on URL</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={2} alignItems="center" mb={2}>
            <CircularProgress size={24} />
            <Typography variant="body2">
              {progressMessage || "Working..."}
            </Typography>
          </Box>
          <LinearProgress />
          <Typography variant="caption" display="block" mt={2}>
            This can take a few minutes while the page is fetched, chunked, and indexed.
          </Typography>
        </DialogContent>
      </Dialog>
      <Typography variant="body2" mb={1}>
        Scrape new or replace URL...
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={8}>
          <TextField
            label="URL"
            size="small"
            fullWidth
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            error={!!urlError}
            helperText={urlError}
            disabled={uploading}
          />
        </Grid>

        <Grid item xs={2}>
          <Button
            size="small"
            variant="contained"
            disabled={uploading}
            onClick={async () => {
              if (url === "") {
                console.error("No url entered");
                return;
              }

              // check url is a valid url using regex
              const urlRegex = new RegExp(
                "^(https?://)?([a-z0-9-]+.)+[a-z0-9]{2,4}.*$"
              );
              if (!urlRegex.test(url)) {
                setUrlError("Please enter a valid URL");
                return;
              } else {
                setUrlError("");
              }

              setLinks([]);
              setUploading(true);
              setProgressMessage("Finding links on page...");
              try {
                const r = await API.post("eventapi", "/mapLinksInPage", {
                  body: {
                    url: url,
                    limit: 100
                  },
                });

                if (r && r.success && r.links) {
                  setLinks(r.links);
                  console.log("Links", r.links);
                }
              } finally {
                setUploading(false);
                setProgressMessage("");
              }
            }}
          >
            Find links
          </Button>
        </Grid>
        <Grid item xs={12}>
          {links.length > 0 && (
            <>
              <Typography variant="body2" mb={1}>
                {`Found ${links?.length ?? 0} Links:`}
              </Typography>
              <List>
                {links.map((link, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={link} />
                    <Box display="flex" justifyContent="flex-end">
                      <ListItemButton
                        disabled={uploading}
                        onClick={() => {
                          window.open(link, "_blank");
                        }}
                      >
                        Preview
                      </ListItemButton>
                      <ListItemButton
                        disabled={uploading}
                        onClick={async () => {
                          // Add your logic to handle the link addition here
                          console.log("Adding link:", link);
                          await scrapeUrl(link);
                        }}
                      >
                        {isUploadedAlready(link) ? "Refresh" : "Add"}
                      </ListItemButton>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default UrlScrape;
